/**
 * Money in minor units (cents): all amounts travel and are
 * stored as integers (bigint in PG / integer in SQLite). Conversion to
 * decimal only occurs at UI boundary.
 */

/** Currencies with decimal digits different from 2 (ISO 4217). */
const CURRENCY_DECIMALS: Record<string, number> = {
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
};

export function currencyDecimals(currency: string): number {
  return CURRENCY_DECIMALS[currency.toUpperCase()] ?? 2;
}

/** Minor unit factor: 10^decimals (100 for EUR, 1 for JPY). */
export function minorUnitFactor(currency: string): number {
  return 10 ** currencyDecimals(currency);
}

/** "12.34" (user decimal) → 1234 (minor units). */
export function toMinorUnits(decimalAmount: number, currency: string): number {
  return Math.round(decimalAmount * minorUnitFactor(currency));
}

/** 1234 (minor units) → 12.34 (decimal for display/editing). */
export function fromMinorUnits(minorAmount: number, currency: string): number {
  return minorAmount / minorUnitFactor(currency);
}

/**
 * Formats minor units with currency symbol according to locale.
 * `signDisplay: "never"` by default — sign is provided by context (color/type).
 */
export function formatMoney(
  minorAmount: number,
  currency: string,
  locale?: string,
  options?: { signDisplay?: "auto" | "never" | "always" | "exceptZero" },
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      signDisplay: options?.signDisplay ?? "never",
    }).format(fromMinorUnits(minorAmount, currency));
  } catch {
    // Non-ISO currency (e.g. custom): flat numeric format + code.
    const decimals = currencyDecimals(currency);
    return `${fromMinorUnits(minorAmount, currency).toFixed(decimals)} ${currency}`;
  }
}

/** Converts between currencies materializing result in minor units. */
export function convertMinorUnits(
  minorAmount: number,
  fromCurrency: string,
  toCurrency: string,
  rate: number,
): number {
  const decimal = fromMinorUnits(minorAmount, fromCurrency) * rate;
  return toMinorUnits(decimal, toCurrency);
}

/** Common currencies list for selector (ISO code). */
export const COMMON_CURRENCIES = [
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "CNY",
  "CHF",
  "CAD",
  "AUD",
  "NZD",
  "MXN",
  "BRL",
  "ARS",
  "CLP",
  "COP",
  "PEN",
  "UYU",
  "VES",
  "DOP",
  "GTQ",
  "CRC",
  "INR",
  "KRW",
  "TWD",
  "HKD",
  "SGD",
  "THB",
  "PHP",
  "IDR",
  "MYR",
  "VND",
  "TRY",
  "ILS",
  "AED",
  "SAR",
  "EGP",
  "ZAR",
  "NGN",
  "MAD",
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "HUF",
  "RON",
  "UAH",
  "RUB",
] as const;
